// client/src/components/ProfilePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaUserCircle, FaEnvelope, FaGlobe, FaTrophy, FaFutbol, FaPaperPlane, 
  FaArrowLeft, FaCommentDots, FaBell, FaChartLine, FaHistory, FaEdit, 
  FaCheckCircle, FaStar, FaTimes, FaUpload, FaClock, FaShieldAlt, FaCopy,
  FaInfoCircle, FaToggleOn, FaToggleOff
} from 'react-icons/fa';
import { getProfile, getUserDailyStats, fetchUserMessages, fetchNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification, updateTeamName, updateGhostSettings } from '../services/api';
import API from '../services/api';

const ProfilePage = ({ userId, onBack }) => {
    const { t, i18n } = useTranslation();
    const [profile, setProfile] = useState(null);
    const [dailyStats, setDailyStats] = useState(null);
    const [messages, setMessages] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notificationLoading, setNotificationLoading] = useState(true);
    const [supportSubject, setSupportSubject] = useState('');
    const [supportMessage, setSupportMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState(null);
    const [sendError, setSendError] = useState(null);
    
    // Career stats and season history
    const [careerStats, setCareerStats] = useState(null);
    const [seasonHistory, setSeasonHistory] = useState([]);
    const [historyCompetition, setHistoryCompetition] = useState('domestic');
    const [statsLoading, setStatsLoading] = useState(true);

    // Team name modal state
    const [showTeamNameModal, setShowTeamNameModal] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [teamNameError, setTeamNameError] = useState('');
    const [teamNameSuccess, setTeamNameSuccess] = useState('');
    const [updatingTeamName, setUpdatingTeamName] = useState(false);

    // ===== GHOST MATCH SETTINGS =====
    const [allowGhostMatches, setAllowGhostMatches] = useState(true);
    const [updatingGhost, setUpdatingGhost] = useState(false);

    // ===== PREMIUM NAME STORE STATE =====
    const [showNameStore, setShowNameStore] = useState(false);
    const [premiumNames, setPremiumNames] = useState([]);
    const [selectedName, setSelectedName] = useState(null);
    const [purchaseStep, setPurchaseStep] = useState('list');
    const [purchaseData, setPurchaseData] = useState(null);
    const [txid, setTxid] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState('');
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);
    const [purchaseError, setPurchaseError] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const fileInputRef = useRef(null);

    // Wallet address (same as Trust Wallet)
    const walletAddress = '0x29a02B2F7B7E0e566199a1e368Ae9Ac47d9D4EA7';

    // ===== GHOST TOGGLE HANDLER =====
    const handleGhostToggle = async () => {
        const newValue = !allowGhostMatches;
        setUpdatingGhost(true);
        try {
            await updateGhostSettings(newValue);
            setAllowGhostMatches(newValue);
            // تحديث البروفايل محلياً أيضاً
            setProfile(prev => ({ ...prev, allow_ghost_matches: newValue }));
        } catch (err) {
            console.error('Failed to update ghost settings:', err);
            alert(t('profile.ghostUpdateError') || 'Failed to update settings. Please try again.');
        } finally {
            setUpdatingGhost(false);
        }
    };

    // ===== LOAD DATA =====
    useEffect(() => {
        const loadData = async () => {
            try {
                const [profileRes, statsRes, messagesRes, notificationsRes] = await Promise.all([
                    getProfile(userId),
                    getUserDailyStats(userId),
                    fetchUserMessages(),
                    fetchNotifications()
                ]);
                setProfile(profileRes.data);
                setDailyStats(statsRes);
                setMessages(messagesRes.data);
                setNotifications(notificationsRes.data);
                // قراءة قيمة الشبح
                if (profileRes.data.allow_ghost_matches !== undefined) {
                    setAllowGhostMatches(profileRes.data.allow_ghost_matches);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
                setNotificationLoading(false);
            }
        };
        loadData();
    }, [userId]);

    // Load career stats and season history
    useEffect(() => {
        const loadStats = async () => {
            setStatsLoading(true);
            try {
                const [careerRes, historyRes] = await Promise.all([
                    API.get(`/statistics/career/${userId}`),
                    API.get(`/statistics/history/${userId}?competition=${historyCompetition}`)
                ]);
                setCareerStats(careerRes.data);
                setSeasonHistory(historyRes.data);
            } catch (err) {
                console.error('Failed to load career stats:', err);
            } finally {
                setStatsLoading(false);
            }
        };
        if (userId) loadStats();
    }, [userId, historyCompetition]);

    // ===== LOAD PREMIUM NAMES WITH STATUS =====
    const loadPremiumNames = async () => {
        try {
            const res = await API.get('/premium-names');
            const names = res.data;
            const statusPromises = names.map(async (name) => {
                try {
                    const statusRes = await API.get(`/premium-names/purchase-status/${name.id}`);
                    return { ...name, purchase_status: statusRes.data.status || null };
                } catch {
                    return { ...name, purchase_status: null };
                }
            });
            const namesWithStatus = await Promise.all(statusPromises);
            setPremiumNames(namesWithStatus);
        } catch (err) {
            console.error('Failed to load premium names:', err);
        }
    };

    // ===== SELECT NAME - SHOW PAYMENT DETAILS =====
    const handleSelectName = async (nameId) => {
        try {
            const res = await API.post('/premium-names/initiate-purchase', { nameId });
            setPurchaseData(res.data);
            setSelectedName(nameId);
            setPurchaseStep('payment');
            setPurchaseError(null);
            setTxid('');
            setUploadedFile(null);
            setUploadedFileName('');
        } catch (err) {
            setPurchaseError(err.response?.data?.error || 'Failed to initiate purchase');
        }
    };

    // ===== SUBMIT PAYMENT VERIFICATION WITH FILE =====
    const handleSubmitPayment = async () => {
        if (!txid.trim()) {
            setPurchaseError('Please enter the Transaction ID (TxID)');
            return;
        }

        setSubmitting(true);
        setPurchaseError(null);

        try {
            const formData = new FormData();
            formData.append('nameId', selectedName);
            formData.append('txid', txid.trim());
            if (uploadedFile) {
                formData.append('screenshot', uploadedFile);
            }

            const res = await API.post('/premium-names/verify-payment', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setPurchaseSuccess(true);
            setPurchaseStep('success');
            
            setPremiumNames(prev => prev.map(n => 
                n.id === selectedName ? { ...n, is_purchased_by_user: true, purchase_status: 'pending' } : n
            ));

            if (res.data.teamName) {
                setProfile(prev => ({ 
                    ...prev, 
                    team_name: res.data.teamName,
                    is_premium_team: true
                }));
            }
        } catch (err) {
            setPurchaseError(err.response?.data?.error || 'Verification failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ===== HANDLE FILE UPLOAD =====
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedFile(file);
            setUploadedFileName(file.name);
        }
    };

    // ===== RESET PURCHASE =====
    const resetPurchase = () => {
        setPurchaseStep('list');
        setSelectedName(null);
        setPurchaseData(null);
        setTxid('');
        setUploadedFile(null);
        setUploadedFileName('');
        setPurchaseSuccess(false);
        setPurchaseError(null);
    };

    // ===== COPY ADDRESS =====
    const copyAddress = () => {
        navigator.clipboard.writeText(walletAddress);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    const handleSendSupport = async (e) => {
        e.preventDefault();
        if (!supportSubject.trim() || !supportMessage.trim()) {
            setSendError(t('profile.supportErrorFields'));
            return;
        }
        setSending(true);
        setSendError(null);
        setSendSuccess(null);
        try {
            await API.post('/support/send', {
                subject: supportSubject,
                message: supportMessage
            });
            setSendSuccess(t('profile.supportSuccess'));
            setSupportSubject('');
            setSupportMessage('');
            const messagesRes = await fetchUserMessages();
            setMessages(messagesRes.data);
        } catch (err) {
            setSendError(err.response?.data?.error || t('profile.supportErrorSend'));
        } finally {
            setSending(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await markNotificationRead(id);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch (err) {
            console.error(err);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteNotification = async (id) => {
        try {
            await deleteNotification(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (err) {
            console.error(err);
        }
    };

// ===== HANDLE TEAM NAME UPDATE =====
const handleUpdateTeamName = async (e) => {
    e.preventDefault();
    setTeamNameError('');
    setTeamNameSuccess('');
    setUpdatingTeamName(true);

    const name = newTeamName.trim();

    // التحقق من صحة الاسم محلياً
    if (!name) {
        setTeamNameError(t('profile.teamNameRequired'));
        setUpdatingTeamName(false);
        return;
    }
    if (name.length < 3 || name.length > 30) {
        setTeamNameError(t('profile.teamNameLength'));
        setUpdatingTeamName(false);
        return;
    }
    if (!/^[a-zA-Z0-9 _\-]+$/.test(name)) {
        setTeamNameError(t('profile.teamNameInvalid'));
        setUpdatingTeamName(false);
        return;
    }

    // ✅ خريطة تحويل المفاتيح من صيغة الخادم إلى صيغة العميل
    const keyMap = {
        'team_name.alreadyChanged': 'teamNameAlreadyChanged',
        'team_name.sameAsCurrent': 'teamNameSameAsCurrent',
        'team_name.alreadyTaken': 'teamNameTaken',
        'team_name.premiumReserved': 'teamNamePremiumReserved',
        'team_name.updateError': 'teamNameUpdateError',
        'team_name.updatedSuccess': 'teamNameUpdated',
    };

    try {
        const res = await updateTeamName(name);
        if (res.data.success) {
            // نجاح التحديث
            const msgKey = res.data.message;
            if (msgKey && typeof msgKey === 'string' && msgKey.startsWith('team_name.')) {
                const translationKey = keyMap[msgKey] || msgKey;
                setTeamNameSuccess(t(translationKey));
            } else {
                setTeamNameSuccess(msgKey || t('profile.teamNameUpdated'));
            }
            setProfile(prev => ({
                ...prev,
                team_name: res.data.teamName,
                team_name_changed: true
            }));
            setTimeout(() => {
                setShowTeamNameModal(false);
                setNewTeamName('');
                setTeamNameSuccess('');
            }, 1500);
        }
    } catch (err) {
        // ✅ استخراج رسالة الخطأ من الاستجابة
        const errorKey = err.response?.data?.error;
        console.log('🔴 Server error key:', errorKey); // للتشخيص

        if (errorKey && typeof errorKey === 'string') {
            // محاولة الترجمة عبر الخريطة
            const translationKey = keyMap[errorKey] || errorKey;
            const translated = t(translationKey);

            // إذا لم يتم العثور على الترجمة، استخدم رسالة افتراضية
            if (translated === translationKey) {
                // ✅ رسائل محددة بناءً على المفتاح الأصلي (حتى لو لم توجد ترجمة)
                switch (errorKey) {
                    case 'team_name.alreadyChanged':
                        setTeamNameError(t('profile.teamNameAlreadyChanged'));
                        break;
                    case 'team_name.sameAsCurrent':
                        setTeamNameError(t('profile.teamNameSameAsCurrent'));
                        break;
                    case 'team_name.alreadyTaken':
                        setTeamNameError(t('profile.teamNameTaken'));
                        break;
                    case 'team_name.premiumReserved':
                        setTeamNameError(t('profile.teamNamePremiumReserved'));
                        break;
                    default:
                        setTeamNameError(t('profile.teamNameError'));
                }
            } else {
                setTeamNameError(translated);
            }
        } else {
            // إذا لم يكن هناك مفتاح خطأ، نعرض رسالة عامة
            setTeamNameError(t('profile.teamNameError'));
        }
    } finally {
        setUpdatingTeamName(false);
    }
};

    if (loading) {
        return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">{t('common.loading')}</div>;
    }

    if (!profile) {
        return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">{t('profile.notFound')}</div>;
    }

    const displayTeamName = profile.team_name || profile.username;

    // ===== Get tier badge =====
    const getTierBadge = (tier) => {
        switch(tier) {
            case 'gold': return { icon: '👑', color: 'text-yellow-400', bg: 'bg-yellow-500/20', label: 'Gold' };
            case 'silver': return { icon: '⭐', color: 'text-gray-300', bg: 'bg-gray-500/20', label: 'Silver' };
            case 'bronze': return { icon: '⭐', color: 'text-amber-500', bg: 'bg-amber-500/20', label: 'Bronze' };
            default: return { icon: '⭕', color: 'text-gray-500', bg: 'bg-gray-500/10', label: 'Common' };
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-emerald-950 text-white p-3 sm:p-6" dir={i18n.dir()}>
            <div className="max-w-4xl mx-auto">
                <button 
                    onClick={onBack} 
                    className="mb-4 sm:mb-6 flex items-center gap-2 bg-black/30 hover:bg-black/50 px-3 sm:px-4 py-2 rounded-full transition text-sm sm:text-base"
                >
                    <FaArrowLeft className="text-sm sm:text-base" /> {t('common.back')}
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                    
                    {/* Profile Info Card */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-black/40 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-yellow-500/30 w-full overflow-hidden"
                    >
                        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                            <div className="bg-yellow-500/20 p-3 sm:p-4 rounded-full flex-shrink-0">
                                <FaUserCircle className="text-4xl sm:text-5xl text-yellow-400" />
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-xl sm:text-2xl font-bold truncate">{profile.username}</h2>
                                <p className="text-gray-300 text-xs sm:text-sm truncate">{t('profile.memberSince')} {new Date(profile.created_at).toLocaleDateString()}</p>
                            </div>
                        </div>

                        <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <FaEnvelope className="text-yellow-400 flex-shrink-0" />
                                <span className="truncate">{profile.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <FaGlobe className="text-yellow-400 flex-shrink-0" />
                                <span>{profile.continent}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <FaTrophy className="text-yellow-400 flex-shrink-0" />
                                <span>{t('profile.points')}: <span className="font-bold text-yellow-300">{profile.total_points || 0}</span></span>
                            </div>
                        </div>

                        {/* Ghost Match Toggle */}
                        <div className="mt-4 pt-3 border-t border-white/10">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <FaShieldAlt className="text-yellow-400 text-sm sm:text-base" />
                                    <div>
                                        <p className="text-xs sm:text-sm font-medium text-white">
                                            {t('profile.ghostMatches') || 'Allow Ghost Matches'}
                                        </p>
                                        <p className="text-[10px] sm:text-xs text-gray-400">
                                            {allowGhostMatches 
                                                ? (t('profile.ghostEnabled') || 'Your team can be used when you\'re offline')
                                                : (t('profile.ghostDisabled') || 'Your team will not be used offline')}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleGhostToggle}
                                    disabled={updatingGhost}
                                    className="flex items-center gap-2 text-sm font-medium transition-all focus:outline-none disabled:opacity-50"
                                >
                                    {allowGhostMatches ? (
                                        <FaToggleOn className="text-3xl text-green-400 hover:text-green-300" />
                                    ) : (
                                        <FaToggleOff className="text-3xl text-gray-400 hover:text-gray-300" />
                                    )}
                                    <span className="hidden sm:inline">
                                        {allowGhostMatches ? t('common.on') : t('common.off')}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Team Name Section with Buy Premium Button */}
                        <div className="mt-4 pt-3 border-t border-white/10">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-3 min-w-0">
                                    <FaFutbol className="text-yellow-400 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-400">{t('profile.teamName')}</p>
                                        <p className="font-bold text-base sm:text-lg truncate">{displayTeamName}</p>
                                        {profile.is_premium_team && (
                                            <span className="text-[10px] text-yellow-400 flex items-center gap-1">
                                                <FaStar className="text-[8px]" /> Premium
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-shrink-0">
                                    {!profile.team_name_changed && (
                                        <button
                                            onClick={() => setShowTeamNameModal(true)}
                                            className="bg-yellow-500/20 hover:bg-yellow-500/30 p-2 rounded-full transition"
                                            title={t('profile.changeTeamName')}
                                        >
                                            <FaEdit className="text-yellow-400 text-sm sm:text-base" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { loadPremiumNames(); setShowNameStore(true); }}
                                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-3 py-2 rounded-full text-xs sm:text-sm transition flex items-center gap-2"
                                    >
                                        <FaStar className="text-yellow-300 text-xs" /> {t('profile.buyPremiumName')}
                                    </button>
                                </div>
                            </div>
                            {profile.team_name_changed && (
                                <span className="text-xs text-green-400 flex items-center gap-1 mt-1">
                                    <FaCheckCircle className="text-sm" /> {t('profile.teamNameChangedOnce')}
                                </span>
                            )}
                        </div>

                        {/* Daily Matches */}
                        <div className="mt-4 sm:mt-6 pt-4 border-t border-white/10">
                            <h3 className="font-semibold text-base sm:text-lg mb-3">📊 {t('profile.dailyMatches')}</h3>
                            <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center">
                                <div className="bg-green-900/50 rounded-lg p-2">
                                    <div className="text-xl sm:text-2xl font-bold text-green-400">{dailyStats.domestic}</div>
                                    <div className="text-[10px] sm:text-xs">{t('competition.domestic')}</div>
                                </div>
                                <div className="bg-yellow-900/50 rounded-lg p-2">
                                    <div className="text-xl sm:text-2xl font-bold text-yellow-400">{dailyStats.champions}</div>
                                    <div className="text-[10px] sm:text-xs">{t('competition.champions')}</div>
                                </div>
                                <div className="bg-blue-900/50 rounded-lg p-2">
                                    <div className="text-xl sm:text-2xl font-bold text-blue-400">{dailyStats.europa}</div>
                                    <div className="text-[10px] sm:text-xs">{t('competition.europa')}</div>
                                </div>
                            </div>
                        </div>

                        {/* Career Statistics */}
                        <div className="mt-4 sm:mt-6 pt-4 border-t border-white/10">
                            <h3 className="font-semibold text-base sm:text-lg mb-3 flex items-center gap-2">
                                <FaChartLine className="text-yellow-400" /> {t('profile.careerStats')}
                            </h3>
                            {statsLoading ? (
                                <p className="text-gray-400 text-sm">{t('common.loading')}</p>
                            ) : careerStats ? (
                                <div className="grid grid-cols-2 gap-2 sm:gap-3 text-center">
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-lg sm:text-xl font-bold text-blue-400">{careerStats.total_matches || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.matches')}</div>
                                    </div>
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-lg sm:text-xl font-bold text-green-400">{careerStats.total_goals_scored || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.goalsScored')}</div>
                                    </div>
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-lg sm:text-xl font-bold text-red-400">{careerStats.total_goals_conceded || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.goalsConceded')}</div>
                                    </div>
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-base sm:text-lg font-bold text-yellow-400">{careerStats.total_wins || 0}-{careerStats.total_draws || 0}-{careerStats.total_losses || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.wins')}-{t('profile.draws')}-{t('profile.losses')}</div>
                                    </div>
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-lg sm:text-xl font-bold text-yellow-600">{careerStats.total_yellow_cards || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.yellowCards')}</div>
                                    </div>
                                    <div className="bg-black/40 rounded-lg p-2">
                                        <div className="text-lg sm:text-xl font-bold text-red-500">{careerStats.total_red_cards || 0}</div>
                                        <div className="text-[10px] sm:text-xs">{t('profile.redCards')}</div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-400 text-sm">{t('profile.noCareerData')}</p>
                            )}
                        </div>
                    </motion.div>

                    {/* Right Column */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-black/40 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-yellow-500/30 w-full overflow-hidden"
                    >
                        {/* Notifications Section */}
                        <div>
                            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                                <h3 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                                    <FaBell className="text-sm sm:text-base" /> {t('notifications.title')}
                                    {notifications.some(n => !n.is_read) && (
                                        <span className="bg-red-500 text-[10px] sm:text-xs px-2 py-0.5 rounded-full">{notifications.filter(n => !n.is_read).length} {t('notifications.new')}</span>
                                    )}
                                </h3>
                                {notifications.some(n => !n.is_read) && (
                                    <button onClick={handleMarkAllRead} className="text-xs text-yellow-400 hover:underline">{t('notifications.markAllRead')}</button>
                                )}
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-1 mb-4 sm:mb-6">
                                {notificationLoading ? (
                                    <p className="text-gray-400 text-sm">{t('common.loading')}</p>
                                ) : notifications.length === 0 ? (
                                    <p className="text-gray-400 text-sm">{t('notifications.noNotifications')}</p>
                                ) : (
                                    notifications.map((notif) => (
                                        <div key={notif.id} className={`bg-gray-800/50 rounded-lg p-2 sm:p-3 text-xs sm:text-sm ${!notif.is_read ? 'border-l-4 border-yellow-400' : ''}`}>
                                            <div className="flex justify-between items-start gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-semibold text-yellow-300 text-sm sm:text-base truncate">{notif.title}</p>
                                                    <p className="text-gray-300 mt-1 break-words">{notif.message}</p>
                                                    <p className="text-gray-500 text-[10px] sm:text-xs mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                                                </div>
                                                <div className="flex gap-2 flex-shrink-0 ml-2">
                                                    {!notif.is_read && (
                                                        <button onClick={() => handleMarkRead(notif.id)} className="text-green-400 text-xs hover:underline">{t('notifications.markRead')}</button>
                                                    )}
                                                    <button onClick={() => handleDeleteNotification(notif.id)} className="text-red-400 text-xs hover:underline">{t('notifications.delete')}</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Technical Support */}
                        <div className="border-t border-white/10 pt-4">
                            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
                                <FaPaperPlane className="text-sm sm:text-base" /> {t('profile.support')}
                            </h2>
                            <p className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4">
                                {t('profile.supportDesc')}
                            </p>
                            <form onSubmit={handleSendSupport} className="space-y-3 sm:space-y-4">
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">{t('profile.subject')}</label>
                                    <input
                                        type="text"
                                        value={supportSubject}
                                        onChange={(e) => setSupportSubject(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base focus:outline-none focus:border-yellow-400"
                                        placeholder={t('profile.subjectPlaceholder')}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs sm:text-sm mb-1">{t('profile.message')}</label>
                                    <textarea
                                        value={supportMessage}
                                        onChange={(e) => setSupportMessage(e.target.value)}
                                        rows="3"
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base focus:outline-none focus:border-yellow-400"
                                        placeholder={t('profile.messagePlaceholder')}
                                        required
                                    />
                                </div>
                                {sendError && <p className="text-red-400 text-xs sm:text-sm">{sendError}</p>}
                                {sendSuccess && <p className="text-green-400 text-xs sm:text-sm">{sendSuccess}</p>}
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                    {sending ? t('common.loading') : <><FaPaperPlane className="text-sm" /> {t('profile.send')}</>}
                                </button>
                            </form>
                        </div>

                        {/* Season History Section */}
                        <div className="border-t border-white/10 pt-4 mt-4">
                            <h3 className="font-semibold text-base sm:text-lg mb-3 flex items-center gap-2">
                                <FaHistory className="text-sm sm:text-base" /> {t('profile.seasonHistory')}
                            </h3>
                            <div className="flex gap-2 mb-3 flex-wrap">
                                {['domestic', 'champions', 'europa'].map(comp => (
                                    <button
                                        key={comp}
                                        onClick={() => setHistoryCompetition(comp)}
                                        className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold transition ${
                                            historyCompetition === comp
                                                ? 'bg-yellow-500 text-black'
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        {comp === 'domestic' ? t('competition.domestic') : comp === 'champions' ? t('competition.champions') : t('competition.europa')}
                                    </button>
                                ))}
                            </div>
                            {statsLoading ? (
                                <p className="text-gray-400 text-sm">{t('common.loading')}</p>
                            ) : seasonHistory.length === 0 ? (
                                <p className="text-gray-400 text-sm">{t('profile.noSeasonHistory')}</p>
                            ) : (
                                <div className="overflow-x-auto max-h-64 overflow-y-auto">
                                    <table className="min-w-full text-xs sm:text-sm text-white">
                                        <thead className="border-b border-gray-700">
                                            <tr className="text-gray-400 text-[10px] sm:text-xs">
                                                <th className="py-1 pr-2 text-left">{t('profile.season')}</th>
                                                <th className="py-1 pr-2 text-left">{t('profile.rank')}</th>
                                                <th className="py-1 pr-2 text-left">{t('profile.pts')}</th>
                                                <th className="py-1 pr-2 text-left">{t('profile.wins')}-{t('profile.draws')}-{t('profile.losses')}</th>
                                                <th className="py-1 pr-2 text-left">{t('profile.gf')}-{t('profile.ga')}</th>
                                                <th className="py-1 text-left">{t('profile.gd')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {seasonHistory.map((stat, idx) => (
                                                <tr key={idx} className="border-b border-gray-700/50">
                                                    <td className="py-1 pr-2">{stat.season}</td>
                                                    <td className="py-1 pr-2 font-mono">#{stat.position}</td>
                                                    <td className="py-1 pr-2">{stat.points}</td>
                                                    <td className="py-1 pr-2 whitespace-nowrap">{stat.wins}-{stat.draws}-{stat.losses}</td>
                                                    <td className="py-1 pr-2 whitespace-nowrap">{stat.goals_for}-{stat.goals_against}</td>
                                                    <td className={`py-1 ${stat.gd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {stat.gd >= 0 ? `+${stat.gd}` : stat.gd}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* ===== MODAL: Change Team Name ===== */}
            {showTeamNameModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
                    <div className="bg-gray-800 rounded-2xl max-w-md w-full p-4 sm:p-6 border border-yellow-500/30 shadow-2xl">
                        <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{t('profile.changeTeamName')}</h3>
                        <p className="text-xs sm:text-sm text-gray-300 mb-4">{t('profile.teamNameWarning')}</p>
                        <form onSubmit={handleUpdateTeamName}>
                            <input
                                type="text"
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 text-sm sm:text-base text-white focus:outline-none focus:border-yellow-400"
                                placeholder={t('profile.enterTeamName')}
                                autoFocus
                                disabled={updatingTeamName}
                            />
                            {teamNameError && <p className="text-red-400 text-xs sm:text-sm mt-2">{teamNameError}</p>}
                            {teamNameSuccess && <p className="text-green-400 text-xs sm:text-sm mt-2">{teamNameSuccess}</p>}
                            <div className="flex gap-3 mt-5">
                                <button
                                    type="submit"
                                    disabled={updatingTeamName}
                                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition disabled:opacity-50 text-sm sm:text-base"
                                >
                                    {updatingTeamName ? t('common.loading') : t('common.save')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowTeamNameModal(false);
                                        setNewTeamName('');
                                        setTeamNameError('');
                                        setTeamNameSuccess('');
                                    }}
                                    className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-2 rounded-lg transition text-sm sm:text-base"
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ===== MODAL: Premium Name Store ===== */}
            <AnimatePresence>
                {showNameStore && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowNameStore(false)}
                        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-gray-900/95 rounded-2xl w-full max-w-2xl p-6 border border-yellow-500/30 shadow-2xl max-h-[90vh] overflow-y-auto"
                        >
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                    <FaStar className="text-yellow-400" />
                                    {t('profile.premiumNames')}
                                </h2>
                                <button onClick={() => { resetPurchase(); setShowNameStore(false); }} className="text-gray-400 hover:text-white">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            {/* STEP 1: List of available names with status */}
                            {purchaseStep === 'list' && (
                                <div>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Choose a unique team name. Each name is available only once!
                                    </p>
                                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                                        {premiumNames.length === 0 ? (
                                            <p className="text-gray-400 text-center py-8">No premium names available right now.</p>
                                        ) : (
                                            premiumNames.map(name => {
                                                const badge = getTierBadge(name.tier);
                                                const hasPending = name.is_purchased_by_user === true || name.purchase_status === 'pending';
                                                const isApproved = name.purchase_status === 'approved';
                                                const isRejected = name.purchase_status === 'rejected';

                                                let statusLabel = '';
                                                let statusColor = '';
                                                if (hasPending && name.purchase_status === 'pending') {
                                                    statusLabel = '⏳ Pending';
                                                    statusColor = 'text-yellow-400 bg-yellow-500/20';
                                                } else if (isApproved) {
                                                    statusLabel = '✅ Approved';
                                                    statusColor = 'text-green-400 bg-green-500/20';
                                                } else if (isRejected) {
                                                    statusLabel = '❌ Rejected';
                                                    statusColor = 'text-red-400 bg-red-500/20';
                                                }

                                                return (
                                                    <div key={name.id} className="flex items-center justify-between bg-gray-800/50 p-3 rounded-xl border border-gray-700 hover:border-yellow-500/30 transition">
                                                        <div className="flex items-center gap-3">
                                                            <span className={`text-2xl ${badge.color}`}>{badge.icon}</span>
                                                            <div>
                                                                <span className={`font-bold ${badge.color}`}>
                                                                    {name.name}
                                                                </span>
                                                                <span className={`text-xs ml-2 px-2 py-0.5 rounded-full ${badge.bg} ${badge.color}`}>
                                                                    {badge.label}
                                                                </span>
                                                                {statusLabel && (
                                                                    <span className={`text-xs ml-2 px-2 py-0.5 rounded-full ${statusColor}`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-sm font-mono text-yellow-300">${name.price} USDT</span>
                                                            {!hasPending && !isApproved ? (
                                                                <button
                                                                    onClick={() => handleSelectName(name.id)}
                                                                    className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30 transition"
                                                                >
                                                                    {t('profile.buy')}
                                                                </button>
                                                            ) : (
                                                                <span className="px-3 py-1 bg-gray-600/50 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                                                                    {isApproved ? '✅ Owned' : '⏳ Pending'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* STEP 2: Payment details */}
                            {purchaseStep === 'payment' && purchaseData && (
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-3">
                                        {t('profile.completePayment')}: <span className="text-yellow-400">{purchaseData.name}</span>
                                    </h3>
                                    
                                    <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 space-y-3">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">{t('profile.price')}</span>
                                            <span className="text-white font-bold">${purchaseData.price} USDT</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">{t('profile.network')}</span>
                                            <span className="text-white font-mono text-sm">{purchaseData.network}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400 block mb-1">{t('profile.sendToAddress')}</span>
                                            <div className="flex items-center gap-2 bg-gray-900/50 p-2 rounded-lg">
                                                <code className="text-xs text-yellow-300 break-all flex-1 font-mono">
                                                    {walletAddress}
                                                </code>
                                                <button
                                                    onClick={copyAddress}
                                                    className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs hover:bg-yellow-500/30 transition flex items-center gap-1"
                                                >
                                                    <FaCopy className="text-[10px]" />
                                                    {copySuccess ? t('common.copied') : t('common.copy')}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                                            <p className="text-xs text-yellow-300 flex items-start gap-2">
                                                <FaInfoCircle className="text-yellow-400 mt-0.5 flex-shrink-0" />
                                                <span>{t('profile.paymentNote')}</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <label className="text-gray-400 text-sm block mb-1">{t('profile.transactionId')}</label>
                                        <input
                                            type="text"
                                            value={txid}
                                            onChange={(e) => setTxid(e.target.value)}
                                            placeholder="e.g., 0x1234...abcd"
                                            className="w-full bg-gray-900/50 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-yellow-400"
                                        />
                                        <p className="text-[10px] text-gray-500 mt-1">{t('profile.txidHelp')}</p>
                                    </div>

                                    <div className="mt-3">
                                        <label className="text-gray-400 text-sm block mb-1 flex items-center gap-2">
                                            <FaUpload /> {t('profile.uploadScreenshot')} ({t('profile.optional')})
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileUpload}
                                                accept="image/*,.pdf"
                                                className="hidden"
                                            />
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="bg-gray-700 hover:bg-gray-600 px-3 py-2 rounded-lg text-sm transition"
                                            >
                                                {t('profile.chooseFile')}
                                            </button>
                                            {uploadedFileName && (
                                                <span className="text-xs text-gray-400 truncate max-w-[150px]">{uploadedFileName}</span>
                                            )}
                                        </div>
                                    </div>

                                    {purchaseError && (
                                        <p className="text-red-400 text-sm mt-3">{purchaseError}</p>
                                    )}

                                    <div className="flex gap-3 mt-4">
                                        <button
                                            onClick={resetPurchase}
                                            className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg text-white transition"
                                        >
                                            {t('common.back')}
                                        </button>
                                        <button
                                            onClick={handleSubmitPayment}
                                            disabled={submitting || !txid.trim()}
                                            className={`flex-1 py-2 rounded-lg font-bold transition ${
                                                submitting || !txid.trim()
                                                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                                    : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                                            }`}
                                        >
                                            {submitting ? t('common.loading') : t('profile.submitPayment')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* STEP 3: Success message */}
                            {purchaseStep === 'success' && (
                                <div className="text-center py-8">
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                        className="text-6xl mb-4"
                                    >
                                        ✅
                                    </motion.div>
                                    <h3 className="text-2xl font-bold text-green-400 mb-2">
                                        {t('profile.purchaseSubmitted')}
                                    </h3>
                                    <p className="text-gray-300 mb-4">
                                        {t('profile.purchasePendingMessage')}
                                    </p>
                                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4 flex items-center gap-3">
                                        <FaClock className="text-yellow-400 text-xl" />
                                        <span className="text-yellow-300 text-sm">
                                            {t('profile.pendingVerification')}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => { resetPurchase(); setShowNameStore(false); }}
                                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-6 py-2 rounded-lg transition"
                                    >
                                        {t('common.close')}
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProfilePage;